import { useLingui } from "@lingui/react/macro";
import { Label } from "@/components/ui/label";
import type { LabelSizeDefinition, LabelSizeKey } from "@/types/label-template";
import { useLabelSizeNames } from "./useLabelSizeNames";

interface LabelSizePickerProps {
  available: LabelSizeDefinition[];
  selected: LabelSizeKey;
  onChange: (size: LabelSizeKey) => void;
  disabled?: boolean;
}

/**
 * Which stock is in the printer.
 *
 * A setting rather than a per-print question (T-1003, transcript 54:00): the
 * stock is a fact about the machine in the corner of the kitchen, and asking a
 * cook to answer it on every sticker is how the wrong size gets picked.
 */
export function LabelSizePicker({
  available,
  selected,
  onChange,
  disabled = false,
}: LabelSizePickerProps) {
  const { t } = useLingui();
  const names = useLabelSizeNames();

  return (
    <fieldset className="flex flex-col gap-2" disabled={disabled}>
      <legend className="pb-2 text-sm font-medium">{t`Label stock`}</legend>
      {available.map((size) => {
        const id = `label-size-${size.key}`;
        return (
          <div
            key={size.key}
            className="flex items-center gap-3 rounded-lg border border-(--color-border) px-3 py-2.5"
          >
            <input
              type="radio"
              id={id}
              name="label-size"
              className="size-4 accent-(--brand-blue)"
              checked={selected === size.key}
              disabled={disabled}
              onChange={() => onChange(size.key)}
            />
            <Label htmlFor={id} className="flex-1 cursor-pointer text-sm font-normal">
              {names[size.key] ?? size.label}
            </Label>
            <span className="text-xs text-(--color-text-secondary)">
              {size.widthIn}&quot; &times; {size.heightIn}&quot;
            </span>
          </div>
        );
      })}
    </fieldset>
  );
}
